import SearchIcon from "@mui/icons-material/Search";
import { Box, ButtonProps, ClickAwayListener, styled, TooltipProps } from "@mui/material";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { unstable_debounce as debounce } from "@mui/utils";
import composeClasses from "@mui/utils/composeClasses";
import { getDataGridUtilityClass, GridFilterModel, gridQuickFilterValuesSelector, useGridApiContext, useGridRootProps } from "@mui/x-data-grid-premium";
import { DataGridProcessedProps, isDeepEqual, useGridSelector } from "@mui/x-data-grid/internals";
import clsx from "clsx";
import PropTypes from "prop-types";
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef } from "react";
import useState from "react-usestateref";

interface OwnerState extends DataGridProcessedProps {
  expanded: boolean;
}

const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ["toolbarQuickFilter"],
  };

  return composeClasses(slots, getDataGridUtilityClass, classes);
};

const defaultSearchValueParser = (searchText: string) => searchText.split(" ").filter((word) => word !== "");

const defaultSearchValueFormatter = (values: string[]) => values.join(" ");

export type GridToolbarSearchFilterProps = TextFieldProps & {
  /**
   * Function responsible for parsing text input in an array of independent values for quick filtering.
   * @param {string} input The value entered by the user
   * @returns {any[]} The array of value on which quick filter is applied
   * @default (searchText: string) => searchText
   *   .split(' ')
   *   .filter((word) => word !== '')
   */
  quickFilterParser?: (input: string) => any[];
  /**
   * Function responsible for formatting values of quick filter in a string when the model is modified
   * @param {any[]} values The new values passed to the quick filter model
   * @returns {string} The string to display in the text field
   * @default (values: string[]) => values.join(' ')
   */
  quickFilterFormatter?: (values: NonNullable<GridFilterModel["quickFilterValues"]>) => string;
  /**
   * The number of milliseconds to wait before updating the search text.
   * @default 500ms.
   */
  debounceMs?: number;
  /**
   * If `true`, search text changes are propagated immediately.
   * @default true.
   */
  quickSearch?: boolean;
  /**
   * If `true`, the search box is shown as a collapsible element.
   * @default false.
   */
  collapsible?: boolean;
  /**
   * If `true`, expanded
   * @default true.
   */
  expanded?: boolean;
  /**
   * Expanded changed
   */
  onExpandedChange?: (newExpand: boolean) => void;
  /**
   * Non-quick search
   */
  onSearchChange?: (newSearchValue: string) => void;
  /**
   * Non-quick search
   */
  onSearchApply?: (newSearchValue: string) => void;
  /**
   * Props to pass to the slot components.
   */
  slotProps?: { button?: Partial<ButtonProps>; tooltip?: Partial<TooltipProps> };
};

const StyledContainer = styled(Box)({
  display: "grid",
  alignItems: "center",
  marginLeft: "auto",
});

const GridToolbarQuickFilterRoot = styled(TextField, {
  name: "MuiDataGrid",
  slot: "ToolbarQuickFilter",
  overridesResolver: (_props, styles) => styles.toolbarQuickFilter,
})<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  gridArea: "1 / 1",
  overflowX: "clip",
  width: ownerState.expanded ? "auto" : "var(--trigger-width, 40px)",
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(["width", "opacity"]),

  paddingBottom: theme.spacing(0.5),
  "& input": {
    marginLeft: theme.spacing(0.5),
  },
  "& .MuiInput-underline:before": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`& input[type="search"]::-webkit-search-decoration,
  & input[type="search"]::-webkit-search-cancel-button,
  & input[type="search"]::-webkit-search-results-button,
  & input[type="search"]::-webkit-search-results-decoration`]: {
    /* clears the 'X' icon from Chrome */
    display: "none",
  },
}));

const GridToolbarSearchFilter = (props: GridToolbarSearchFilterProps) => {
  console.log(`GridToolbarSearchFilter:`, props);
  const rootProps = useGridRootProps();
  const {
    slotProps = {},
    quickFilterParser = defaultSearchValueParser,
    quickFilterFormatter = defaultSearchValueFormatter,
    debounceMs = rootProps.filterDebounceMs,
    className,
    collapsible,
    ...other
  } = props;
  const buttonProps = slotProps.button || {};
  const tooltipProps = slotProps.tooltip || {};
  const searchButtonId = useId();
  const [expanded, setExpanded] = useState(props.collapsible ? (props.expanded ?? false) : true);
  const apiRef = useGridApiContext();
  const quickSearch = props.quickSearch ?? true;
  const ownerState: OwnerState = { ...rootProps, expanded };
  const classes = useUtilityClasses(ownerState);
  const quickFilterValues = useGridSelector(apiRef, gridQuickFilterValuesSelector);
  const [searchValue, setSearchValue] = useState(() => (quickFilterFormatter(quickFilterValues ?? []) || props.value || "") as string);
  const prevQuickFilterValuesRef = useRef(quickFilterValues);

  useEffect(() => {
    if (!isDeepEqual(prevQuickFilterValuesRef.current, quickFilterValues)) {
      // The model of quick filter value has been updated
      prevQuickFilterValuesRef.current = quickFilterValues;

      // Update the input value if needed to match the new model
      setSearchValue((prevSearchValue) => (isDeepEqual(quickFilterParser(prevSearchValue), quickFilterValues) ? prevSearchValue : quickFilterFormatter(quickFilterValues ?? [])));
    }
  }, [quickFilterValues, quickFilterFormatter, quickFilterParser]);

  const updateSearchValue = useCallback(
    (newSearchValue: string) => {
      console.log(`updateSearchValue:`, newSearchValue);
      const newQuickFilterValues = quickFilterParser(newSearchValue);
      prevQuickFilterValuesRef.current = newQuickFilterValues;
      apiRef.current.setQuickFilterValues(newQuickFilterValues);
    },
    [apiRef, quickFilterParser],
  );

  const debouncedUpdateSearchValue = useMemo(() => debounce(updateSearchValue, debounceMs), [updateSearchValue, debounceMs]);
  useEffect(() => debouncedUpdateSearchValue.clear, [debouncedUpdateSearchValue]);

  const handleSearchValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      console.log(`handleSearchValueChange:`, event.target.value);
      const newSearchValue = event.target.value;
      setSearchValue(newSearchValue);
      props.onSearchChange?.(newSearchValue);
      if (quickSearch) {
        debouncedUpdateSearchValue(newSearchValue);
      }
    },
    [debouncedUpdateSearchValue, quickSearch],
  );

  const handleSearchReset = useCallback(() => {
    console.log(`handleSearchReset`);
    setSearchValue("");
    updateSearchValue("");
    if (!quickSearch) {
      props.onSearchChange?.("");
      props.onSearchApply?.("");
    }
  }, [updateSearchValue]);

  const handleSearchClick = () => {
    console.log("handleSearchClick: ", searchValue);
    if (!expanded) {
      setExpanded(true);
      props.onExpandedChange?.(true);
    } else {
      if (quickSearch) {
        debouncedUpdateSearchValue(searchValue);
      } else {
        props.onSearchApply?.(searchValue);
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    console.log("handleKeyDown: ", event.key);

    if (!quickSearch && event.key === "Enter") {
      props.onSearchApply?.(searchValue);
    }
    if (props.collapsible && event.key === "Escape") {
      setExpanded(false);
      props.onExpandedChange?.(false);
    }
  };

  const handleClickAway = () => {
    console.log("handleClickAway: ", searchValue);
    if (props.collapsible && !searchValue) {
      setExpanded(false);
      props.onExpandedChange?.(false);
    }
  };

  console.log("searchValue: ", searchValue);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <StyledContainer>
        <rootProps.slots.baseTooltip title={apiRef.current.getLocaleText("toolbarSearchLabel" as any)} enterDelay={1000} {...rootProps.slotProps?.baseTooltip} {...tooltipProps}>
          <rootProps.slots.baseIconButton
            id={searchButtonId}
            size="small"
            color="primary"
            aria-label={apiRef.current.getLocaleText("toolbarQuickFilterLabel")}
            onClick={handleSearchClick}
            {...rootProps.slotProps?.baseIconButton}
            {...buttonProps}
            sx={{
              gridArea: "1 / 1",
              zIndex: 1,
              width: "30px",
            }}
          >
            <SearchIcon fontSize="small" />
          </rootProps.slots.baseIconButton>
        </rootProps.slots.baseTooltip>
        <GridToolbarQuickFilterRoot
          as={rootProps.slots.baseTextField}
          ownerState={ownerState}
          variant="standard"
          inputRef={(input) => {
            expanded && input && input.focus();
          }}
          value={searchValue}
          onChange={handleSearchValueChange}
          onKeyDown={handleKeyDown}
          className={clsx(classes.root, className)}
          placeholder={apiRef.current.getLocaleText("toolbarQuickFilterPlaceholder")}
          aria-label={apiRef.current.getLocaleText("toolbarQuickFilterLabel")}
          type="search"
          {...other}
          slotProps={{
            input: {
              endAdornment: (
                <rootProps.slots.baseIconButton
                  aria-label={apiRef.current.getLocaleText("toolbarQuickFilterDeleteIconLabel")}
                  size="small"
                  sx={[
                    searchValue
                      ? {
                          visibility: "visible",
                        }
                      : {
                          visibility: "hidden",
                        },
                  ]}
                  onClick={handleSearchReset}
                  {...rootProps.slotProps?.baseIconButton}
                >
                  <rootProps.slots.quickFilterClearIcon fontSize="small" />
                </rootProps.slots.baseIconButton>
              ),
            },
            ...slotProps,
          }}
          {...rootProps.slotProps?.baseTextField}
          sx={{
            paddingLeft: "30px",
          }}
        />
      </StyledContainer>
    </ClickAwayListener>
  );
};

GridToolbarSearchFilter.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The debounce time in milliseconds.
   * @default 150
   */
  debounceMs: PropTypes.number,
  /**
   * Function responsible for formatting values of quick filter in a string when the model is modified
   * @param {any[]} values The new values passed to the quick filter model
   * @returns {string} The string to display in the text field
   * @default (values: string[]) => values.join(' ')
   */
  quickFilterFormatter: PropTypes.func,
  /**
   * Function responsible for parsing text input in an array of independent values for quick filtering.
   * @param {string} input The value entered by the user
   * @returns {any[]} The array of value on which quick filter is applied
   * @default (searchText: string) => searchText
   *   .split(' ')
   *   .filter((word) => word !== '')
   */
  quickFilterParser: PropTypes.func,
  /**
   * Flag indicating whether the quick search feature is enabled.
   * @default false
   */
  quickSearch: PropTypes.bool,
  /**
   * Apply search
   */
  onExpandedChange: PropTypes.func,

  /**
   * Apply search
   */
  onSearchChange: PropTypes.func,
  /**
   * Apply search
   */
  onSearchApply: PropTypes.func,
  /**
   * Additional slotProps passed to the TextField component
   */
  slotProps: PropTypes.object,
} as any;

export default GridToolbarSearchFilter;
